import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  MapPin, Wallet, Clock, Calendar, Users, ArrowLeft, MessageCircle,
  Play, CheckCircle2, XCircle, Flag, Check, X, Star, Zap,
} from "lucide-react";
import { api, rupiah, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { StarRating, StarInput, ReputationBadge } from "@/components/StarRating";
import { Loading, EmptyState } from "@/components/EmptyState";
import { ReportModal } from "@/components/ReportModal";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fileUrl } from "@/lib/api";
import { toast } from "sonner";

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [rateFor, setRateFor] = useState(null);
  const [applicantSort, setApplicantSort] = useState("recent");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/jobs/${id}`);
      setJob(data);
      const rv = await api.get(`/jobs/${id}/reviews`);
      setReviews(rv.data);
      if (data.is_owner) {
        const ap = await api.get(`/jobs/${id}/applications`);
        setApplicants(ap.data);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn) => { setBusy(true); try { await fn(); await load(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } };

  const apply = () => act(async () => { await api.post(`/jobs/${id}/apply`); toast.success("Lamaran berhasil dikirim!"); });
  const cancelApp = () => act(async () => { await api.post(`/applications/${job.my_application.id}/cancel`); toast.success("Lamaran dibatalkan"); });
  const transition = (status, msg) => act(async () => { await api.post(`/jobs/${id}/transition`, { status }); toast.success(msg); });
  const accept = (appId) => act(async () => { await api.post(`/applications/${appId}/accept`); toast.success("Pelamar diterima"); });
  const reject = (appId) => act(async () => { await api.post(`/applications/${appId}/reject`); toast.success("Pelamar ditolak"); });
  const complete = () => act(async () => { await api.post(`/jobs/${id}/complete`); toast.success("Konfirmasi selesai terkirim"); });
  const feature = () => act(async () => { await api.post(`/jobs/${id}/feature`, { featured: !job.featured }); toast.success(job.featured ? "Tanda mendesak dihapus" : "Ditandai sebagai mendesak"); });
  const extendFeature = () => act(async () => { await api.post(`/jobs/${id}/feature`, { featured: true }); toast.success("Tanda Mendesak diperpanjang 24 jam"); });

  if (loading) return <Loading />;
  if (!job) return <EmptyState title="Pekerjaan tidak ditemukan" />;

  const acceptedApplicants = applicants.filter((a) => a.status === "ACCEPTED");
  const myReviewGiven = reviews.some((r) => r.reviewer_id === user.id);
  const isAcceptedWorker = job.my_application?.status === "ACCEPTED";
  const iConfirmed = (job.completions || []).includes(user.id);
  const featuredHoursLeft = job.featured && job.featured_until ? Math.max(0, Math.round((new Date(job.featured_until) - Date.now()) / 3600000)) : null;
  const canChat = (job.is_owner && job.accepted_count > 0) || isAcceptedWorker;

  // who can I rate? participants except me
  const rateTargets = [];
  if (job.status === "COMPLETED") {
    if (job.is_owner) acceptedApplicants.forEach((a) => rateTargets.push(a.worker));
    else if (isAcceptedWorker) rateTargets.push(job.employer);
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="job-detail">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-slate-500 mb-4 hover:text-slate-800"><ArrowLeft size={18} /> Kembali</button>

      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <span className="inline-block bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-sm font-medium mb-2">{job.category?.name}</span>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">{job.title}</h1>
          </div>
          <StatusBadge status={job.status} />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-6">
          <Info icon={Wallet} label="Bayaran" value={rupiah(job.payment)} highlight />
          <Info icon={Clock} label="Durasi" value={job.duration} />
          <Info icon={Calendar} label="Tanggal" value={formatDate(job.date)} />
          <Info icon={Clock} label="Mulai" value={job.start_time} />
          <Info icon={Users} label="Pekerja" value={`${job.accepted_count}/${job.workers_needed}`} />
          <Info icon={MapPin} label="Kota" value={job.city + (job.distance_km != null ? ` · ${job.distance_km} km` : "")} />
        </div>

        <div className="mt-6">
          <p className="text-xs font-bold text-slate-500 uppercase mb-1">Alamat</p>
          <p className="text-slate-700">{job.address}, {job.city}</p>
        </div>
        <div className="mt-4">
          <p className="text-xs font-bold text-slate-500 uppercase mb-1">Deskripsi</p>
          <p className="text-slate-700 whitespace-pre-wrap">{job.description}</p>
        </div>

        {/* Employer */}
        <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate(`/pengguna/${job.employer.id}`)} data-testid="employer-link">
            <Avatar className="w-11 h-11">
              <AvatarImage src={fileUrl(job.employer.photo_url)} />
              <AvatarFallback className="bg-blue-100 text-blue-700 font-bold">{job.employer.name?.[0]}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold text-slate-900 flex items-center gap-1.5">{job.employer.name}{job.employer.reputation && <ReputationBadge level={job.employer.reputation} />}</p>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <StarRating value={job.employer.rating_average} count={job.employer.rating_count} size={13} />
                <span>· {job.employer.completed_jobs} selesai</span>
              </div>
            </div>
          </div>
          {!job.is_owner && (
            <Button data-testid="report-btn" variant="ghost" size="sm" onClick={() => setReportOpen(true)} className="text-slate-400 hover:text-rose-600">
              <Flag size={16} />
            </Button>
          )}
        </div>
      </div>

      {/* Worker actions */}
      {!job.is_owner && (
        <div className="mt-5">
          {!job.my_application && job.status === "OPEN" && (
            <Button data-testid="apply-btn" onClick={apply} disabled={busy} className="w-full h-12 bg-blue-600 hover:bg-blue-700 rounded-lg text-base font-semibold">
              Lamar Pekerjaan
            </Button>
          )}
          {job.my_application?.status === "PENDING" && (
            <div className="flex gap-3">
              <Button disabled className="flex-1 h-12 bg-amber-100 text-amber-700 rounded-lg font-semibold" data-testid="applied-badge">Lamaran Terkirim</Button>
              <Button data-testid="cancel-app-btn" onClick={cancelApp} disabled={busy} variant="outline" className="h-12 rounded-lg">Batalkan</Button>
            </div>
          )}
          {isAcceptedWorker && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center text-emerald-700 font-semibold" data-testid="accepted-badge">
              ✓ Anda Diterima untuk pekerjaan ini
            </div>
          )}
          {isAcceptedWorker && job.status === "IN_PROGRESS" && (
            iConfirmed ? (
              <div className="mt-3 text-sm font-semibold text-amber-600 flex items-center gap-1.5" data-testid="worker-waiting-confirm"><CheckCircle2 size={16} /> Menunggu konfirmasi selesai dari pemberi kerja</div>
            ) : (
              <div className="mt-3">
                <ConfirmBtn testId="worker-complete-btn"
                  trigger={<Button disabled={busy} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-base font-semibold"><CheckCircle2 size={16} className="mr-2" /> Konfirmasi Selesai</Button>}
                  title="Konfirmasi pekerjaan sudah selesai?" desc="Setelah kedua pihak konfirmasi, pekerjaan akan ditandai selesai dan rating tersedia." onConfirm={complete} />
              </div>
            )
          )}
          {job.my_application?.status === "REJECTED" && (
            <Button disabled className="w-full h-12 bg-slate-100 text-slate-500 rounded-lg" data-testid="rejected-badge">Lamaran Belum Diterima</Button>
          )}
          {!job.my_application && job.status !== "OPEN" && (
            <Button disabled className="w-full h-12 bg-slate-100 text-slate-500 rounded-lg" data-testid="closed-badge">Pekerjaan Ditutup</Button>
          )}
        </div>
      )}

      {/* Chat button */}
      {canChat && (
        <Button data-testid="chat-btn" onClick={() => navigate(`/pesan/${id}`)} className="w-full h-12 mt-3 bg-slate-900 hover:bg-slate-800 rounded-lg text-base font-semibold">
          <MessageCircle size={18} className="mr-2" /> Buka Chat
        </Button>
      )}

      {/* Employer management */}
      {job.is_owner && (
        <>
          <div className="mt-5 flex flex-wrap gap-3">
            {job.status === "DRAFT" && (
              <Button data-testid="publish-draft-btn" onClick={() => transition("OPEN", "Pekerjaan dipublikasikan")} disabled={busy} className="bg-blue-600 hover:bg-blue-700 rounded-lg">Publikasikan</Button>
            )}
            {job.status === "ASSIGNED" && (
              <ConfirmBtn testId="start-btn" trigger={<Button disabled={busy} className="bg-amber-500 hover:bg-amber-600 rounded-lg"><Play size={16} className="mr-2" /> Mulai Pekerjaan</Button>}
                title="Mulai pekerjaan?" desc="Status pekerjaan akan menjadi Berjalan." onConfirm={() => transition("IN_PROGRESS", "Pekerjaan dimulai")} />
            )}
            {job.status === "IN_PROGRESS" && !iConfirmed && (
              <ConfirmBtn testId="complete-btn" trigger={<Button disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 rounded-lg"><CheckCircle2 size={16} className="mr-2" /> Tandai Selesai</Button>}
                title="Apakah Anda yakin pekerjaan ini sudah selesai?" desc="Pekerja juga harus mengonfirmasi sebelum pekerjaan ditandai selesai." onConfirm={complete} />
            )}
            {job.status === "IN_PROGRESS" && iConfirmed && (
              <div className="text-sm font-semibold text-amber-600 flex items-center gap-1.5" data-testid="employer-waiting-confirm"><CheckCircle2 size={16} /> Menunggu konfirmasi selesai dari pekerja</div>
            )}
            {["OPEN", "ASSIGNED", "DRAFT"].includes(job.status) && (
              <Button data-testid="feature-btn" onClick={feature} disabled={busy} variant="outline" className={`rounded-lg ${job.featured ? "text-amber-600 border-amber-300 bg-amber-50" : ""}`}>
                <Zap size={16} className="mr-2" /> {job.featured ? "Batalkan Mendesak" : "Tandai Mendesak"}
              </Button>
            )}
            {job.featured && featuredHoursLeft != null && ["OPEN", "ASSIGNED", "DRAFT"].includes(job.status) && (
              <Button data-testid="extend-feature-btn" onClick={extendFeature} disabled={busy} variant="outline" className="rounded-lg text-amber-700 border-amber-300 bg-amber-50">
                <Zap size={16} className="mr-2" /> Perpanjang 24 jam (sisa {featuredHoursLeft} jam)
              </Button>
            )}
            {["OPEN", "ASSIGNED"].includes(job.status) && (
              <ConfirmBtn testId="cancel-job-btn" trigger={<Button disabled={busy} variant="outline" className="rounded-lg text-rose-600 border-rose-200 hover:bg-rose-50"><XCircle size={16} className="mr-2" /> Batalkan</Button>}
                title="Batalkan pekerjaan?" desc="Pekerjaan tidak akan aktif lagi." onConfirm={() => transition("CANCELLED", "Pekerjaan dibatalkan")} />
            )}
          </div>

          {/* Applicants */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4 gap-2">
              <h2 className="text-xl font-bold">Pelamar ({applicants.length})</h2>
              {applicants.length > 1 && (
                <select data-testid="applicant-sort" value={applicantSort} onChange={(e) => setApplicantSort(e.target.value)}
                  className="h-9 rounded-lg border border-slate-200 bg-white text-sm px-2 text-slate-700">
                  <option value="recent">Terbaru</option>
                  <option value="rating">Rating tertinggi</option>
                  <option value="distance">Terdekat</option>
                </select>
              )}
            </div>
            {applicants.length === 0 ? (
              <EmptyState title="Belum ada pelamar" description="Bagikan pekerjaanmu agar lebih banyak yang melamar." />
            ) : (
              <div className="space-y-3">
                {[...applicants].sort((a, b) => {
                  if (applicantSort === "rating") return (b.worker.rating_average || 0) - (a.worker.rating_average || 0);
                  if (applicantSort === "distance") return (a.distance_km ?? Infinity) - (b.distance_km ?? Infinity);
                  return 0;
                }).map((a) => (
                  <div key={a.id} data-testid={`applicant-${a.id}`} className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0 cursor-pointer" onClick={() => navigate(`/pengguna/${a.worker.id}`)}>
                        <Avatar className="w-11 h-11">
                          <AvatarImage src={fileUrl(a.worker.photo_url)} />
                          <AvatarFallback className="bg-blue-100 text-blue-700 font-bold">{a.worker.name?.[0]}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900 truncate flex items-center gap-1.5">{a.worker.name}{a.worker.reputation && <ReputationBadge level={a.worker.reputation} />}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <StarRating value={a.worker.rating_average} count={a.worker.rating_count} size={12} />
                            <span>· {a.worker.completed_jobs} selesai</span>
                            {a.distance_km != null && <span>· {a.distance_km} km</span>}
                          </div>
                        </div>
                      </div>
                      <StatusBadge status={a.status} kind="app" />
                    </div>
                    {a.worker.skills?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {a.worker.skills.map((s) => <span key={s} className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full text-xs">{s}</span>)}
                      </div>
                    )}
                    {a.status === "PENDING" && job.status === "OPEN" && (
                      <div className="flex gap-2 mt-3">
                        <Button data-testid={`accept-${a.id}`} onClick={() => accept(a.id)} disabled={busy} size="sm" className="bg-emerald-600 hover:bg-emerald-700 rounded-lg flex-1"><Check size={15} className="mr-1" /> Terima</Button>
                        <Button data-testid={`reject-${a.id}`} onClick={() => reject(a.id)} disabled={busy} size="sm" variant="outline" className="rounded-lg flex-1 text-rose-600 border-rose-200 hover:bg-rose-50"><X size={15} className="mr-1" /> Tolak</Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Rating section */}
      {job.status === "COMPLETED" && rateTargets.length > 0 && !myReviewGiven && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-2xl p-5" data-testid="rate-section">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><Star className="text-amber-500 fill-amber-400" size={18} /> Berikan Rating</h3>
          <p className="text-sm text-slate-600 mt-1">Pekerjaan selesai. Beri rating untuk membangun kepercayaan.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {rateTargets.map((t) => (
              <Button key={t.id} data-testid={`rate-${t.id}`} onClick={() => setRateFor(t)} className="bg-blue-600 hover:bg-blue-700 rounded-lg">Rating {t.name}</Button>
            ))}
          </div>
        </div>
      )}

      {/* Reviews */}
      {reviews.length > 0 && (
        <div className="mt-6">
          <h3 className="font-bold text-slate-900 mb-3">Ulasan</h3>
          <div className="space-y-3">
            {reviews.map((r) => (
              <div key={r.id} className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={fileUrl(r.reviewer?.photo_url)} />
                      <AvatarFallback className="bg-blue-100 text-blue-700 text-xs font-bold">{r.reviewer?.name?.[0]}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{r.reviewer?.name || "Pengguna"}</p>
                      {r.reviewed_user?.name && <p className="text-xs text-slate-400">menilai {r.reviewed_user.name}</p>}
                    </div>
                  </div>
                  <StarRating value={r.rating} showCount={false} size={14} />
                </div>
                {r.comment && <p className="text-slate-700 mt-2 text-sm">{r.comment}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      <ReportModal open={reportOpen} onOpenChange={setReportOpen} targetJobId={job.id} targetUserId={job.employer.id} />
      <RateDialog job={job} target={rateFor} onClose={() => setRateFor(null)} onDone={load} />
    </div>
  );
}

function Info({ icon: Icon, label, value, highlight }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={18} className={`mt-0.5 shrink-0 ${highlight ? "text-emerald-600" : "text-blue-600"}`} />
      <div><p className="text-xs text-slate-400">{label}</p><p className={`font-semibold text-sm ${highlight ? "text-emerald-700 text-base" : "text-slate-800"}`}>{value}</p></div>
    </div>
  );
}

function ConfirmBtn({ trigger, title, desc, onConfirm, testId }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild><span data-testid={testId}>{trigger}</span></AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{desc}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Batal</AlertDialogCancel>
          <AlertDialogAction data-testid={`${testId}-confirm`} onClick={onConfirm} className="bg-blue-600 hover:bg-blue-700">Ya, lanjutkan</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function RateDialog({ job, target, onClose, onDone }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  if (!target) return null;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/reviews`, { reviewed_user_id: target.id, rating, comment });
      toast.success("Rating terkirim!");
      onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="rate-dialog">
        <DialogHeader><DialogTitle>Beri Rating untuk {target.name}</DialogTitle></DialogHeader>
        <div className="flex justify-center py-2"><StarInput value={rating} onChange={setRating} /></div>
        <Textarea data-testid="rate-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Tulis ulasan (opsional)" rows={3} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button data-testid="rate-submit" onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700">{busy ? "Mengirim..." : "Kirim Rating"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
